import {
  AloftArea,
  AloftPrice,
  expected,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getDepreciatedCostPageRoute } from '../utils/getDepreciatedCostPageRoute.ts';

export const largestSiteHasHighestValue: AlertDefinition = ({
  compSequence,
  order,
  propertyDataEntities,
  siteValues,
}) => {
  const compSites = compSequence
    .map((id, i) => ({
      id,
      name: `Comparable ${i + 1}`,
      size: orUndefined(schAloftArea.parse)(
        (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData.editableData.site
          .area,
      ),
      value: siteValues[id],
    }))
    .filter(
      (c): c is { id: string; name: string; size: AloftArea; value: AloftPrice } =>
        isDefined(c.size) && isDefined(c.value),
    );

  if (compSites.length < 2) return undefined;

  const maxSiteValue = expected(AloftPrice.max(compSites.map((c) => c.value)));
  const maxSiteArea = expected(AloftArea.max(compSites.map((c) => c.size)));

  const largestSites = compSites.filter((c) => c.size?.equals(maxSiteArea));

  // We allow a 5% tolerance on the max site value to account for minor variations
  const largestSiteWithoutMaxSiteValue = largestSites.find((c) =>
    c.value?.lt(maxSiteValue.mul(0.95)),
  );
  if (!largestSiteWithoutMaxSiteValue) return undefined;

  const highestValueSite = expected(compSites.find((c) => c.value?.equals(maxSiteValue)));

  return {
    type: schAlertType.enum.largestSiteHasHighestValue,
    status: 'active',
    severity: 'error',
    orderType: ['saas', 'appraisify'],
    message: `The largest Site Size (${largestSiteWithoutMaxSiteValue.name}: ${largestSiteWithoutMaxSiteValue.size?.toString()} | ${largestSiteWithoutMaxSiteValue.value?.toString()}) does not have the highest Site Value (${highestValueSite.name}: ${highestValueSite.size?.toString()} | ${highestValueSite.value?.toString()}) among the properties in the analysis.`,
    propertyID: largestSiteWithoutMaxSiteValue.id,
    fixingInfo: {
      tips: 'Tips: In a Sales Comparison with similar lots, larger lots typically command higher prices. Provide clear justification if you believe the current entry is appropriate due to unique factors in the market (e.g., location, zoning, topography, access, utilities, demand).',
      pairedCostRouteItem: getDepreciatedCostPageRoute(order),
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
    },
    triggers: [],
  };
};
